/**
 * Sumber tunggal STATUS PASIEN IGD.
 *
 * Dipakai bersama oleh IgdStatusChip (warna/ikon/label) dan CardListPasien
 * (item filter + nilai default).
 *
 * Cara penentuan tiap status didokumentasikan di docs/USER_GUIDELINE.md §3.0a.
 *
 * `value` HARUS identik dengan key `igd_indicator.status` dari
 * GET /poli/01/daftar-pasien dan dengan nilai query `?status=`.
 */

export type IgdStatusKey =
  | "belum_diperiksa"
  | "dalam_perawatan"
  | "menunggu_ruang"
  | "selesai"
  | "batal";

export interface IgdStatusMeta {
  value: IgdStatusKey;
  label: string;
  /** Label ringkas untuk chip di tabel & item filter (kolom hanya 150px). */
  shortLabel: string;
  color: string;
  textColor: string;
  icon: string;
  /** Pasien masih berada fisik di IGD (jam LOS masih berjalan). */
  losRunning: boolean;
}

export const IGD_STATUS_META: Record<IgdStatusKey, IgdStatusMeta> = {
  belum_diperiksa: {
    value: "belum_diperiksa",
    label: "Belum Diperiksa",
    shortLabel: "Belum Diperiksa",
    color: "red darken-1",
    textColor: "white",
    icon: "mdi-timer-sand",
    losRunning: true
  },
  dalam_perawatan: {
    value: "dalam_perawatan",
    label: "Dalam Perawatan",
    shortLabel: "Dalam Perawatan",
    color: "blue lighten-4",
    textColor: "blue darken-3",
    icon: "mdi-stethoscope",
    losRunning: true
  },
  menunggu_ruang: {
    value: "menunggu_ruang",
    label: "Menunggu Ruang (Pindah Bangsal)",
    shortLabel: "Menunggu Ruang",
    color: "amber darken-2",
    textColor: "white",
    icon: "mdi-bed-clock",
    losRunning: true
  },
  selesai: {
    value: "selesai",
    label: "Selesai",
    shortLabel: "Selesai",
    color: "green",
    textColor: "white",
    icon: "mdi-check-circle-outline",
    losRunning: false
  },
  batal: {
    value: "batal",
    label: "Batal Periksa",
    shortLabel: "Batal",
    color: "grey darken-1",
    textColor: "white",
    icon: "mdi-close-circle-outline",
    losRunning: false
  }
};

export const IGD_STATUS_ORDER: IgdStatusKey[] = [
  "belum_diperiksa",
  "dalam_perawatan",
  "menunggu_ruang",
  "selesai",
  "batal"
];

export const IGD_STATUS_LIST: IgdStatusMeta[] = IGD_STATUS_ORDER.map(
  key => IGD_STATUS_META[key]
);

/** Nilai filter "Semua" — dipakai juga oleh unit non-IGD. */
export const IGD_STATUS_FILTER_ALL = "-1";

/** Default halaman Daftar Pasien IGD saat pertama dibuka. */
export const IGD_DEFAULT_STATUS_FILTER = "belum_diperiksa";

export const IGD_STATUS_FILTER_OPTIONS: Array<{
  text: string;
  value: string;
}> = [{ text: "Semua", value: IGD_STATUS_FILTER_ALL }].concat(
  IGD_STATUS_LIST.map(it => ({ text: it.shortLabel, value: it.value as string }))
);

/**
 * Status rilis lama (payload yang masih tersimpan di memori klien saat transisi
 * rilis) → status baru, supaya chip tidak kosong. Alias TIDAK boleh dikirim ke `?status=`.
 */
const LEGACY_STATUS_ALIAS: Record<string, IgdStatusKey> = {
  aktif: "dalam_perawatan",
  boarding: "menunggu_ruang",
  pindah_ruang: "selesai",
  pulang_rumah: "selesai",
  pulang_paksa: "selesai",
  dirujuk: "selesai",
  meninggal: "selesai"
};

export function normalizeIgdStatus(
  status?: string | null
): IgdStatusKey | null {
  if (!status) return null;
  if (Object.prototype.hasOwnProperty.call(IGD_STATUS_META, status)) {
    return status as IgdStatusKey;
  }
  return LEGACY_STATUS_ALIAS[status] || null;
}

export function getIgdStatusMeta(status?: string | null): IgdStatusMeta | null {
  const key = normalizeIgdStatus(status);
  return key ? IGD_STATUS_META[key] : null;
}

/** Nilai filter yang boleh dikirim ke `?status=` untuk unit IGD. */
export function isIgdStatusFilterValue(value: unknown): boolean {
  const v = String(value === null || value === undefined ? "" : value);
  return (
    v === IGD_STATUS_FILTER_ALL ||
    Object.prototype.hasOwnProperty.call(IGD_STATUS_META, v)
  );
}
